// JWT Helper Functions

/**
 * Dekodiert ein JWT Token ohne Signaturprüfung
 * Wichtig: Dies prüft NICHT die Signatur! Verwenden Sie dies nur für Client-seitige Anzeige.
 * Die tatsächliche Autorisierung muss immer auf dem Server erfolgen.
 */
export function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    // JWT besteht aus drei Teilen: Header.Payload.Signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Ungültiges JWT-Format');
      return null;
    }

    // Der Payload ist der zweite Teil (Base64-URL-kodiert)
    const payload = parts[1];

    // Base64-URL zu Base64 konvertieren
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

    // Base64 dekodieren
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Fehler beim Dekodieren des JWT:', error);
    return null;
  }
}

/**
 * Extrahiert Rollen aus einem JWT Token
 * Unterstützt verschiedene Claim-Namen: roles, realm_access.roles, resource_access
 */
export function extractRolesFromToken(token: string): string[] {
  const decoded = decodeJwt(token);
  if (!decoded) return [];

  const roles: string[] = [];

  // Direkte Rollen
  if (Array.isArray(decoded.roles)) {
    roles.push(...(decoded.roles as string[]));
  }

  // Keycloak Realm Rollen
  if (decoded.realm_access && typeof decoded.realm_access === 'object') {
    const realmAccess = decoded.realm_access as Record<string, unknown>;
    if (Array.isArray(realmAccess.roles)) {
      roles.push(...(realmAccess.roles as string[]));
    }
  }

  // Resource Access (Client-spezifische Rollen)
  if (decoded.resource_access && typeof decoded.resource_access === 'object') {
    const resourceAccess = decoded.resource_access as Record<string, unknown>;
    Object.values(resourceAccess).forEach((resource) => {
      if (typeof resource === 'object' && resource !== null) {
        const res = resource as Record<string, unknown>;
        if (Array.isArray(res.roles)) {
          roles.push(...(res.roles as string[]));
        }
      }
    });
  }

  return [...new Set(roles)]; // Duplikate entfernen
}

/**
 * Extrahiert Gruppen aus einem JWT Token
 */
export function extractGroupsFromToken(token: string): string[] {
  const decoded = decodeJwt(token);
  if (!decoded) return [];

  if (Array.isArray(decoded.groups)) {
    return decoded.groups as string[];
  }

  return [];
}
